import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { reportService, normalizeBranchScope, REPORT_TIME_DEFAULTS } from '../../services/reportService';
import { useAppStore } from '../../store/appStore';
import ReportActionBar from '../../components/reports/ReportActionBar';

const formatDate = (date) => {
  const d = new Date(date);
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

const formatTime = (value) => {
  if (!value) return '';
  const [hourText, minuteText] = value.split(':');
  const hour = Number(hourText);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minuteText || '00'} ${period}`;
};

const formatHeaderRange = (startDate, endDate, fromTime, toTime) => {
  const startText = `${startDate || ''}${fromTime ? ` ${formatTime(fromTime)}` : ''}`.trim();
  const endText = `${endDate || ''}${toTime ? ` ${formatTime(toTime)}` : ''}`.trim();
  return `Report Period: ${startText || 'N/A'} → ${endText || 'N/A'}`;
};

const ItemsSummary = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { branch: selectedBranch } = useAppStore();
  const params = new URLSearchParams(location.search);
  const startDateParam = params.get('from_date') || params.get('start_date');
  const endDateParam = params.get('to_date') || params.get('end_date');
  const requestedBranchId = params.get('branch_id');
  const branchId = normalizeBranchScope(requestedBranchId || selectedBranch || 'all');
  const fromTimeParam = params.get('from_time') || '';
  const toTimeParam = params.get('to_time') || '';

  const today = formatDate(new Date());
  const initialStartDate = startDateParam || today;
  const initialEndDate = endDateParam || today;
  const initialFromTime = fromTimeParam || REPORT_TIME_DEFAULTS.fromTime;
  const initialToTime = toTimeParam || REPORT_TIME_DEFAULTS.toTime;

  const allColumns = [
    { label: 'Item', value: 'product_name' },
    { label: 'Category', value: 'category_name' },
    { label: 'Quantity', value: 'total_quantity', numeric: true },
    { label: 'Amount', value: 'total_sales', numeric: true },
    { label: 'Order Count', value: 'order_count', numeric: true },
  ];

  const [draftStartDate, setDraftStartDate] = useState(initialStartDate);
  const [draftEndDate, setDraftEndDate] = useState(initialEndDate);
  const [draftFromTime, setDraftFromTime] = useState(initialFromTime);
  const [draftToTime, setDraftToTime] = useState(initialToTime);
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: initialStartDate,
    endDate: initialEndDate,
    fromTime: initialFromTime,
    toTime: initialToTime,
  });
  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);
  const [orderType, setOrderType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hiddenColumns.length === 0) {
      setHiddenColumns(['category_name', 'order_count']);
    }
  }, [hiddenColumns.length]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await reportService.getBranchItemsSummary(
          appliedFilters.startDate,
          appliedFilters.endDate,
          branchId,
          appliedFilters.fromTime,
          appliedFilters.toTime,
          orderType,
        );
        const payload = response?.data?.data || {};
        const rows = Array.isArray(payload) ? payload : (payload.items_summary || payload.items || []);
        setData(Array.isArray(rows) ? rows : []);
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || 'Network error');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [appliedFilters, branchId, orderType]);

  const handleApply = () => {
    if ((draftFromTime || draftToTime) && (!draftFromTime || !draftToTime)) {
      setError('Both from_time and to_time are required when using time filtering.');
      return false;
    }

    if (draftFromTime && draftToTime && draftFromTime > draftToTime) {
      setError('from_time must be less than or equal to to_time.');
      return false;
    }

    setError('');
    setAppliedFilters({
      startDate: draftStartDate,
      endDate: draftEndDate,
      fromTime: draftFromTime,
      toTime: draftToTime,
    });
    return true;
  };

  const handleReset = () => {
    const defaults = {
      startDate: today,
      endDate: today,
      fromTime: REPORT_TIME_DEFAULTS.fromTime,
      toTime: REPORT_TIME_DEFAULTS.toTime,
    };

    setDraftStartDate(defaults.startDate);
    setDraftEndDate(defaults.endDate);
    setDraftFromTime(defaults.fromTime);
    setDraftToTime(defaults.toTime);
    setAppliedFilters(defaults);
    setError('');
  };

  const groupedData = useMemo(() => {
    const filtered = data.filter((row) => {
      if (!orderType || orderType === 'all') return true;
      const ot = (row.order_type || row.orderType || '').toString().toLowerCase();
      if (orderType === 'dine_in') return ot === 'dine in' || ot === 'dine-in' || ot === 'dine_in' || ot === 'dinein';
      if (orderType === 'pickup') return ot === 'pickup' || ot === 'pick up' || ot === 'pick_up';
      if (orderType === 'delivery') return ot === 'delivery';
      return true;
    });

    const agg = new Map();
    filtered.forEach((row) => {
      const productId = row.product_id ?? null;
      const itemName = (row.product_name || row.item || '').toString().trim();
      const key = productId ? String(productId) : itemName.toLowerCase();

      if (!agg.has(key)) {
        agg.set(key, {
          product_id: productId,
          product_name: itemName,
          category_name: row.category_name || row.category || 'Uncategorized',
          total_quantity: 0,
          total_sales: 0,
          orderIds: new Set(),
        });
      }

      const entry = agg.get(key);
      entry.total_quantity += Number(row.total_quantity ?? row.quantity ?? row.qty ?? 0);
      entry.total_sales += Number(row.total_sales ?? row.total_amount ?? row.amount ?? row.total ?? 0);
      const orderId = row.order_id ?? (row.order || row.order_no || row.order_number || null);
      if (orderId != null) entry.orderIds.add(String(orderId));
    });

    const aggregatedItems = Array.from(agg.values()).map((item) => ({
      product_id: item.product_id,
      product_name: item.product_name,
      category_name: item.category_name || 'Uncategorized',
      total_quantity: item.total_quantity,
      total_sales: Number(item.total_sales),
      order_count: item.orderIds.size,
    }));

    const groups = new Map();
    aggregatedItems.forEach((item) => {
      const cat = item.category_name || 'Uncategorized';
      if (!groups.has(cat)) {
        groups.set(cat, { category_name: cat, total_quantity: 0, total_sales: 0, order_count: 0, items: [] });
      }
      const group = groups.get(cat);
      group.items.push(item);
      group.total_quantity += Number(item.total_quantity || 0);
      group.total_sales += Number(item.total_sales || 0);
      group.order_count += Number(item.order_count || 0);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        items: group.items.sort((a, b) => (a.product_name || '').localeCompare(b.product_name || '')),
      }))
      .sort((a, b) => (a.category_name || '').localeCompare(b.category_name || ''));
  }, [data, orderType]);

  const filteredGroups = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return groupedData;

    return groupedData
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          const haystack = `${item.product_name || ''} ${group.category_name || ''}`.toLowerCase();
          return haystack.includes(term);
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [groupedData, searchTerm]);

  const visibleColumns = useMemo(
    () => allColumns.filter((column) => !hiddenColumns.includes(column.value)),
    [allColumns, hiddenColumns],
  );

  const grandTotal = useMemo(
    () => filteredGroups.reduce(
      (acc, group) => ({
        total_quantity: acc.total_quantity + Number(group.total_quantity || 0),
        total_sales: acc.total_sales + Number(group.total_sales || 0),
      }),
      { total_quantity: 0, total_sales: 0 },
    ),
    [filteredGroups],
  );

  const currentBranchLabel = branchId === 'all' ? 'All Outlets' : (selectedBranch?.name || 'Selected Outlet');
  const reportColumns = [
    { label: 'Item', key: 'product_name' },
    { label: 'Category', key: 'category_name' },
    { label: 'Quantity', key: 'total_quantity', type: 'number' },
    { label: 'Amount', key: 'total_sales', type: 'currency' },
    { label: 'Order Count', key: 'order_count', type: 'number' },
  ];
  const reportSummary = [
    { label: 'Total Quantity', value: grandTotal.total_quantity.toLocaleString('en-IN') },
    { label: 'Total Sales', value: `₹${Number(grandTotal.total_sales).toLocaleString('en-IN')}` },
    { label: 'Item Count', value: filteredGroups.reduce((sum, group) => sum + group.items.length, 0).toLocaleString('en-IN') },
  ];

  const formatTimeDisplay = (fromTime, toTime) => {
    if (!fromTime && !toTime) return 'Time Wise';
    return `${fromTime || REPORT_TIME_DEFAULTS.fromTime} - ${toTime || REPORT_TIME_DEFAULTS.toTime}`;
  };

  const filterSummary = useMemo(
    () => formatHeaderRange(appliedFilters.startDate, appliedFilters.endDate, appliedFilters.fromTime, appliedFilters.toTime),
    [appliedFilters.startDate, appliedFilters.endDate, appliedFilters.fromTime, appliedFilters.toTime],
  );

  const summaryFooter = useMemo(() => (
    <Box sx={{ mt: 2 }}> 
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', background: 'linear-gradient(180deg, rgba(255,255,255,1), rgba(248,250,252,1))', boxShadow: '0 8px 18px rgba(15, 23, 42, 0.04)' }}>
        <Box component="tbody">
          <Box component="tr" sx={{ backgroundColor: 'rgba(59, 130, 246, 0.06)' }}>
            <Box component="td" sx={{ p: 1.75, fontWeight: 800, width: '30%' }}>Grand Total</Box>
            <Box component="td" sx={{ p: 1.75, fontWeight: 700, textAlign: 'right', width: '20%' }}>Qty: {grandTotal.total_quantity}</Box>
            <Box component="td" sx={{ p: 1.75, fontWeight: 700, textAlign: 'right', width: '25%' }}>Sales: ₹{Number(grandTotal.total_sales).toLocaleString()}</Box>
            <Box component="td" sx={{ p: 1.75, fontWeight: 700, textAlign: 'right', width: '25%' }}>Orders: {filteredGroups.reduce((sum, group) => sum + Number(group.order_count || 0), 0)}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  ), [filteredGroups, grandTotal]);

  const toggleColumn = (value) => {
    setHiddenColumns((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  const toggleGroup = (index) => {
    setExpandedGroups((prev) => (prev.includes(index) ? prev.filter((item) => item !== index) : [...prev, index]));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(180deg, rgba(255,255,255,1), rgba(248,250,252,1))',
          boxShadow: '0 12px 24px rgba(15, 23, 42, 0.04)',
        }}
      >
        <Stack spacing={2.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em', color: 'text.primary' }}>Item Report</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Category and item sales breakdown</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1.25, ml: 'auto' }}>
              <ReportActionBar
                title="Item Report"
                data={data}
                columns={reportColumns}
                summary={reportSummary}
                startDate={appliedFilters.startDate}
                endDate={appliedFilters.endDate}
                fromTime={appliedFilters.fromTime || REPORT_TIME_DEFAULTS.fromTime}
                toTime={appliedFilters.toTime || REPORT_TIME_DEFAULTS.toTime}
                branchLabel={currentBranchLabel}
                activeFilters={{ 'Order Type': orderType === 'all' ? 'All' : orderType }}
                filename="item-report"
                onBack={() => navigate('/reports')}
              />
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 1,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'rgba(255,255,255,0.8)',
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <TextField size="small" label="From Date" type="date" value={draftStartDate} onChange={(e) => setDraftStartDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 170, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} />
              <TextField size="small" label="To Date" type="date" value={draftEndDate} onChange={(e) => setDraftEndDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 170, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Order Type</InputLabel>
                <Select value={orderType} label="Order Type" onChange={(e) => setOrderType(e.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="dine_in">Dine In</MenuItem>
                  <MenuItem value="pickup">Pickup</MenuItem>
                  <MenuItem value="delivery">Delivery</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={() => { setDraftFromTime(appliedFilters.fromTime || REPORT_TIME_DEFAULTS.fromTime); setDraftToTime(appliedFilters.toTime || REPORT_TIME_DEFAULTS.toTime); setTimeDialogOpen(true); }} startIcon={<CalendarTodayOutlinedIcon />} sx={{ minWidth: 180, textTransform: 'none', borderRadius: 2, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                {formatTimeDisplay(appliedFilters.fromTime, appliedFilters.toTime)}
              </Button>
              <Button variant="contained" onClick={handleApply} sx={{ borderRadius: 2, minWidth: 110 }}>Apply</Button>
              <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2, minWidth: 110 }}>Reset</Button>
            </Box>
          </Paper>

          {filterSummary && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
                label={filterSummary}
                color="primary"
                variant="outlined"
                sx={{ borderRadius: 2, px: 1, py: 0.5, fontWeight: 600 }}
              />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
            <TextField
              size="small"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} /> }}
              sx={{ minWidth: 220, maxWidth: 260, width: { xs: '100%', sm: 'auto' } }}
            />
            <Button variant="outlined" onClick={() => setColumnDialogOpen(true)} sx={{ borderRadius: 2 }}>Configure Column</Button>
          </Box>

          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>Loading report...</Box>
          ) : filteredGroups.length === 0 ? (
            <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider', background: 'linear-gradient(180deg, rgba(248,250,252,0.9) 0%, rgba(255,255,255,1) 100%)' }}>
              <Box sx={{ fontSize: 40, mb: 1.5 }}>📄</Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>No Records Found</Typography>
              <Typography variant="body2" color="text.secondary">Try selecting a different date or time range.</Typography>
            </Paper>
          ) : (
            <Box>
              {filteredGroups.map((group, index) => {
                const isExpanded = expandedGroups.includes(index);
                return (
                  <Paper key={`${group.category_name}-${index}`} sx={{ mb: 2, overflow: 'hidden', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, background: 'linear-gradient(180deg, rgba(248,250,252,1), rgba(241,245,249,1))' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{group.category_name || 'Uncategorized'}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">Qty: {group.total_quantity}</Typography>
                        <Typography variant="body2" color="text.secondary">Amount: {group.total_sales}</Typography>
                        <IconButton size="small" onClick={() => toggleGroup(index)}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                    </Box>

                    {isExpanded && (
                      <Box sx={{ p: 1.5 }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                          <Box component="thead">
                            <Box component="tr" sx={{ backgroundColor: '#F8FAFC' }}>
                              {visibleColumns.map((column) => (
                                <Box component="th" key={column.value} sx={{ p: 1.25, textAlign: column.numeric ? 'right' : 'left', fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>
                                  {column.label}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                          <Box component="tbody">
                            {group.items.map((item, itemIndex) => (
                              <Box component="tr" key={`${item.product_name || 'item'}-${itemIndex}`} sx={{ '&:nth-of-type(even)': { backgroundColor: 'rgba(148, 163, 184, 0.04)' } }}>
                                {visibleColumns.map((column) => {
                                  const value = item[column.value];
                                  const cellValue = column.value === 'total_sales' ? `₹${Number(value || 0).toLocaleString()}` : (column.numeric ? Number(value || 0).toLocaleString() : (value ?? ''));
                                  return (
                                    <Box component="td" key={`${column.value}-${itemIndex}`} sx={{ p: 1.25, borderBottom: '1px solid', borderColor: 'divider', textAlign: column.numeric ? 'right' : 'left' }}>
                                      {cellValue}
                                    </Box>
                                  );
                                })}
                              </Box>
                            ))}
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Sub Total Qty: {group.total_quantity} | Sub Total Amount: {group.total_sales}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}

          {summaryFooter}
        </Stack>
      </Paper>

      <Dialog open={timeDialogOpen} onClose={() => setTimeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Time Wise</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="From Time" type="time" value={draftFromTime} onChange={(e) => setDraftFromTime(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }} />
          <TextField label="To Time" type="time" value={draftToTime} onChange={(e) => setDraftToTime(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => { const ok = handleApply(); if (ok) setTimeDialogOpen(false); }}>Apply</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={columnDialogOpen} onClose={() => setColumnDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Configure Column</DialogTitle>
        <DialogContent>
          <FormGroup>
            {allColumns.map((column) => (
              <FormControlLabel
                key={column.value}
                control={<Checkbox checked={!hiddenColumns.includes(column.value)} onChange={() => toggleColumn(column.value)} />}
                label={column.label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemsSummary;
